
import Vue from 'vue';
import { Component, InjectReactive, Ref } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { getRouteName, getUrlParamsFromFilterState } from '@/helpers/state-helper';

import { CollectionType } from '@/enums/collection-type';

import { FilterCollection } from '@/stores/filter.store';

import { Content } from '@/interfaces/components/content';

import PublicLists from '@/components/watchlist/public-lists/PublicLists.vue';
import MyLists from '@/components/watchlist/my-lists/MyLists.vue';
import TVShowTracking from '@/components/watchlist/tv-show-tracking/TVShowTracking.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import NavigationTab from '@/components/nav/NavigationTab.vue';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { Genres } from '@/constants/types';
import Provider from '@/interfaces/provider';
import { GenresByShortNameConstant } from '@/constants/ConstantsProvider.vue';
import { ListsCollectionType } from '@/stores/routing.store';

const Filter = namespace('filter');
const Constant = namespace('constant');

type ListsTab = {
	key: 'my_lists' | 'public_lists' | 'tv_show_tracking';
	translation: string;
	collectionType: ListsCollectionType;
};

@Component({
	name: 'Watchlist',
	components: {
		NavigationTab,
		HiddenHorizontalScrollbar,
		PublicLists,
		MyLists,
		TVShowTracking,
	},
})
export default class Watchlist extends Vue {
	@InjectReactive({ from: GenresByShortNameConstant, default: [] }) readonly genresByShortName: Genres;

	@Filter.Getter filters: (collectionType: CollectionType) => FilterCollection;

	@Constant.Getter providersByShortName: Record<string, Provider>;

	@Ref('content') readonly content!: Content;

	get pageType() {
		return this.$route.path.split('/').pop()?.replaceAll('-', '_') || 'public_lists';
	}

	get watchlistTabs() {
		return [
			{
				key: 'my_lists',
				translation: this.$tc('WEBAPP_MYLISTS'),
				collectionType: CollectionType.MY_LISTS,
			},
			{
				key: 'public_lists',
				translation: this.$tc('WEBAPP_LISTS_PUBLIC_LISTS'),
				collectionType: CollectionType.PUBLIC_LISTS,
			},
			{
				key: 'tv_show_tracking',
				translation: this.$tc('WEBAPP_LISTS_TV_SHOW_TRACKING'),
				collectionType: CollectionType.TV_SHOW_TRACKING,
			},
		] as ListsTab[];
	}

	tabRouteParams(tab: ListsTab) {
		const routeParams = this.routeParams(tab.collectionType);
		return {
			...routeParams,
			query: {
				...routeParams.query,
				list_id: undefined,
			},
		};
	}

	/**
	 * with these parameters we can always link to the correct url for each collectionType page.
	 * in some cases we want to overwrite the currentFilters of the passed CollectionType, that's what `filterOverwrite` handles.
	 */
	routeParams(collectionType: CollectionType, filterOverwrite?: FilterCollection): any {
		const isNewPageCollectionType = [
			CollectionType.COMING_SOON,
			CollectionType.LEAVING_SOON,
			CollectionType.PRICEDROPS,
		].includes(collectionType);

		const filterCollectionType = isNewPageCollectionType ? CollectionType.NEW : collectionType;
		const filters = filterOverwrite ?? this.filters(filterCollectionType);
		const routeName = getRouteName(collectionType, filters);
		const params = getUrlParamsFromFilterState(
			routeName,
			filters,
			this.providersByShortName,
			this.genresByShortName,
			collectionType,
			this.$route.query
		);
		return {
			name: routeName,
			...params,
		};
	}

	isActiveTab(tab: ListsTab) {
		return tab.key === this.pageType;
	}

	scrollToTop() {
		this.content.scrollToTop(0, 'auto');
	}

	onTabClick(tab: ListsTab) {
		this.scrollToTop();

		TrackingHelper.trackEvent('userinteraction', {
			action: 'filter_toggled',
			label: 'list_types',
			property: tab.key,
		});
	}
}
